import { ApiClient } from '@mondough/public-api-request'

import { createLocaleCookie, readLocaleCookie } from './cookie'
import { supportedLanguagesByLocaleCode } from './locales'
import { supportedRegionsByAlpha2RegionCode } from './regions'
import {
  LiveLanguageCodes,
  LiveRegionCodes,
  LiveRegions,
  LocaleManager,
  ResolvedLocale,
  Source,
} from './types'
import { isProductionEnvironment, isSupportedLanguage } from './utils'

export * from './types'
// `market-locale` and `preferred-languages` are re-exported BEFORE
// `export * from './context'` on purpose. context.tsx imports `locale` from
// this barrel, so index<->context is a require cycle; a consumer that spreads
// `jest.requireActual('@mondough/locale')` reads every re-export getter while
// that cycle is mid-flight, and these modules must already be initialised or
// `getMarketLocale` resolves to `undefined` (breaking the consumer's tests).
// (locales/regions/utils below are safe because `types`/`context` require them
// first.)
export { getMarketLocale } from './market-locale'
export {
  getMarketLocaleForSession,
  getPreferredLanguages,
  type LocalePreferenceSource,
  parseAcceptLanguage,
} from './preferred-languages'
export * from './context'
export * from './components'
export { configureLocales, isSupportedLanguage, resetLocales } from './utils'
export { supportedLanguagesByLocaleCode } from './locales'
export {
  imminentRegionsByAlpha2RegionCode,
  supportedRegionsByAlpha2RegionCode,
} from './regions'

// fallback locale
const FALLBACK_LOCALE: ResolvedLocale = {
  localeCode: supportedLanguagesByLocaleCode['en-GB'].localeCode,
  source: Source.FALLBACK,
  fromCookie: false,
}

const DEFAULT_OPTIONS = { ignoreCookie: false } as const

export const locale: LocaleManager = {
  getSupportedLocales() {
    const locales = Object.values(supportedLanguagesByLocaleCode)
    if (isProductionEnvironment()) {
      return locales.filter((locale) => locale.enabledByDefault)
    }
    return locales
  },
  getLocale(localeCode: LiveLanguageCodes) {
    return supportedLanguagesByLocaleCode[localeCode]
  },
  fromRequestServerSide(ctx, options = DEFAULT_OPTIONS) {
    let resolvedLocale: ResolvedLocale | null = null
    const targetRegion = ctx.locale ?? ctx.defaultLocale ?? null
    const acceptLangHeader = ctx.req?.headers['accept-language'] ?? null

    // Nextjs i18n routing returns the alpha2 region, from which we have
    // to obtain the the relevant language-region code
    if (!resolvedLocale && targetRegion && isSupportedRegion(targetRegion)) {
      const region = supportedRegionsByAlpha2RegionCode[targetRegion]
      const lang = defaultLocaleForRegion(region)

      if (lang) {
        const localeCode = supportedLanguagesByLocaleCode[lang].localeCode
        console.info(
          `[lib.locale] Determined locale using Next.js i18n region: ${localeCode}`,
        )
        resolvedLocale = {
          localeCode: localeCode,
          source: Source.NEXTJS_I18N,
          fromCookie: false,
        }
      }
    }

    // if there is no i18n routing try using the accept-language header
    if (!resolvedLocale && acceptLangHeader) {
      const locale = parseAcceptLanguageHeader(acceptLangHeader)
      console.info(
        `[lib.locale] Determined locale from Accept-Language header: ${locale}`,
      )
      if (locale) {
        resolvedLocale = {
          localeCode: locale,
          source: Source.ACCEPT_LANG_HEADER,
          fromCookie: false,
        }
      }
    }

    // Early-out when the caller doesn't want the resolved locale reconciled
    // against the cookie.
    if (resolvedLocale && options.ignoreCookie) {
      return resolvedLocale
    }

    const cookieLocale = options.ignoreCookie
      ? null
      : readLocaleCookie(ctx.req?.headers.cookie)

    if (resolvedLocale) {
      // A higher-precedence cookie wins over the freshly detected locale.
      if (
        cookieLocale &&
        cookieLocale.source.precedence > resolvedLocale.source.precedence
      ) {
        resolvedLocale = cookieLocale
      }
    } else if (cookieLocale) {
      // No i18n region or Accept-Language signal on this request, but the
      // cookie records the user's last known locale (the cross-domain source
      // of truth). Prefer it over guessing the fallback.
      resolvedLocale = cookieLocale
    }

    if (resolvedLocale) {
      // Persist the resolved locale via the response headers. This resolver is
      // server-only; client-side resolution goes through fromBrowser, which
      // writes document.cookie directly.
      ctx.res?.setHeader('Set-Cookie', createLocaleCookie(resolvedLocale))

      return resolvedLocale
    }

    console.warn(
      `[lib.locale] Unable to determine locale, using fallback: ${FALLBACK_LOCALE.localeCode}`,
    )
    return FALLBACK_LOCALE
  },

  fromBrowser(options = DEFAULT_OPTIONS) {
    let browserLocale: ResolvedLocale | null = null

    browserLocale = isSupportedLanguage(navigator.language)
      ? {
          localeCode:
            supportedLanguagesByLocaleCode[navigator.language].localeCode,
          source: Source.BROWSER,
        }
      : null

    // browser language takes precedence over the fallback
    let resolvedLocale = browserLocale ?? FALLBACK_LOCALE

    // we don't care whats in the cookie so return what we have
    if (options.ignoreCookie) {
      return resolvedLocale
    }

    // now let's check what's in the cookie
    const cookieLocale = readLocaleCookie(document.cookie)
    if (
      cookieLocale &&
      // is the cookie data higher precedence
      cookieLocale.source.precedence > resolvedLocale.source.precedence
    ) {
      resolvedLocale = cookieLocale
    }

    const updatedLocaleCookie = createLocaleCookie(resolvedLocale)
    document.cookie = updatedLocaleCookie

    return resolvedLocale
  },

  async fromUserPreferencesClientSide(apiClient: ApiClient, country: string) {
    try {
      const data = await apiClient
        .authenticated()
        .url(`/i18n-preferences`)
        .get()
        .json(parseI18nPreferences)

      if (data && country) {
        const localeCode = data.locales_by_country[country]?.locale_code
        return {
          localeCode: (localeCode ??
            FALLBACK_LOCALE.localeCode) as LiveLanguageCodes,
          source: Source.USER_PREFERENCE,
        }
      }

      return FALLBACK_LOCALE
    } catch (error) {
      console.warn('[lib.locale] Failed to fetch user preferences:', error)
      return FALLBACK_LOCALE
    }
  },

  persistExternalLocale(ctx, localeCode) {
    // Normalise to standard ll-RR format (e.g. en-GB, es-ES)
    const normalised = normaliseLocaleCode(localeCode)

    if (!isSupportedLanguage(normalised)) {
      return
    }

    const resolvedLocale: ResolvedLocale = {
      localeCode: normalised,
      source: Source.NEXTJS_I18N,
      fromCookie: false,
    }

    const localeCookie = createLocaleCookie(resolvedLocale)

    // Server-side: append to Set-Cookie response headers
    if (ctx.res && !ctx.res.headersSent) {
      const existing = ctx.res.getHeader('Set-Cookie') ?? []
      const cookies = Array.isArray(existing) ? existing : [String(existing)]
      ctx.res.setHeader('Set-Cookie', [...cookies, localeCookie])
      return
    }

    // Client-side: write directly to document.cookie (e.g. during
    // Next.js client-side route transitions where ctx.res is undefined)
    if (typeof window !== 'undefined') {
      document.cookie = localeCookie
    }
  },
}

function parseAcceptLanguageHeader(
  language: string | undefined,
): LiveLanguageCodes | null {
  if (language) {
    const matches = language
      .split(',')
      .map((l) => {
        const [localeCode, _] = l.trim().split(';')
        const locale = localeCode?.trim()
        if (isSupportedLanguage(locale)) {
          return locale
        } else {
          return null
        }
      })
      .filter(Boolean)

    if (matches.length) {
      return matches[0] as LiveLanguageCodes
    }
  }

  return null
}

function defaultLocaleForRegion(region: LiveRegions): LiveLanguageCodes {
  const regionAlpha2 = region.alpha2
  const defaultLangCode = region.defaultLanguageCode

  const locale = `${defaultLangCode}-${regionAlpha2}`
  const entries = Object.entries(supportedLanguagesByLocaleCode)
  return entries.map(([key, _]) => key).includes(locale)
    ? (locale as LiveLanguageCodes)
    : 'en-GB'
}

function isSupportedRegion(
  region: string | undefined,
): region is LiveRegionCodes {
  if (region && region in supportedRegionsByAlpha2RegionCode) {
    return true
  }

  return false
}

interface I18nPreferences {
  locales_by_country: Record<string, { locale_code: string }>
}

function parseI18nPreferences(data: unknown): I18nPreferences {
  if (!isObject(data)) {
    throw new Error('Expected object')
  }
  const locales = data.locales_by_country
  if (!isObject(locales)) {
    throw new Error('Expected locales_by_country object')
  }
  for (const value of Object.values(locales)) {
    if (!isObject(value) || typeof value.locale_code !== 'string') {
      throw new Error('Expected locale_code string')
    }
  }
  return data as unknown as I18nPreferences
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Normalises a locale code to the standard ll-RR format
 * (lowercase language, uppercase region). E.g. "EN-gb" -> "en-GB"
 */
function normaliseLocaleCode(localeCode: string): string {
  const [lang, region] = localeCode.split('-')
  if (!lang || !region) {
    return localeCode
  }
  return `${lang.toLowerCase()}-${region.toUpperCase()}`
}
